/**
 * Página pública de inscrição em curso — Sprint 1B IHTP.
 *
 * Acesso via /cursos/inscricao/:token (sem autenticação). O token mapeia
 * para uma `curso.Trel_LinkInscricao` que define o curso + nº de vagas +
 * sponsor. Cada participante preenche o seu próprio formulário, gerando
 * uma `Tinf_FichaInscricao` ligada ao mesmo `ID_LinkInscricao`.
 *
 * Fluxo:
 *   1. GET metadata → mostra curso + lista fichas + vagas
 *   2. POST nova ficha (NIF/Email obrigatórios; cliente externo criado
 *      com sponsor = comprador da encomenda)
 *   3. PUT para editar até a ficha estar bloqueada (após upload PDF)
 *   4. Download PDF Ficha + Contrato + Menor (se aplicável)
 *   5. Upload PDF assinado por cada documento
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { verifyEmail } from "@/shared/emailValidation";

const API = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (s) => {
  if (!s) return "";
  try { return new Date(s).toLocaleDateString("pt-PT"); } catch { return s; }
};

const apiGet = (path) =>
  fetch(`${API}/api/public/cursos/inscricao${path}`, { credentials: "omit" })
    .then(async (r) => {
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`);
      return j;
    });

const apiJson = (path, method, body) =>
  fetch(`${API}/api/public/cursos/inscricao${path}`, {
    method, credentials: "omit",
    headers: { "Content-Type": "application/json" },
    body: body ? JSON.stringify(body) : undefined,
  }).then(async (r) => {
    const j = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`);
    return j;
  });

const apiUpload = (path, file) => {
  const fd = new FormData();
  fd.append("file", file);
  return fetch(`${API}/api/public/cursos/inscricao${path}`, {
    method: "POST", credentials: "omit", body: fd,
  }).then(async (r) => {
    const j = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`);
    return j;
  });
};

// ─────────────────────────────────────────────────────────────────────

export default function CursoInscricaoPublicPage() {
  const { token } = useParams();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null); // ficha sendo editada/criada
  const [qrFicha, setQrFicha] = useState(null); // ficha cujo QR está a ser mostrado

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try { setOverview(await apiGet(`/${token}`)); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { reload(); }, [reload]);

  if (loading) return <div className="p-8 text-center text-sm text-[#6B7280]">A carregar…</div>;
  if (error) return (
    <div className="min-h-screen bg-[#FAF8F2] p-4 sm:p-8">
      <div className="max-w-2xl mx-auto">
        <img src="/logo-ihtp-dark.svg" alt="IHTP" className="h-9 w-auto mb-6" data-testid="ihtp-logo-error" />
        <h1 className="text-xl font-bold text-[#0F1829] mb-2">Link inválido</h1>
        <p className="text-sm text-[#6B7280]">{error}</p>
        <p className="text-xs mt-4 text-[#6B7280]">Se acredita que isto é um erro, contacte <a className="underline" href="mailto:ihavethepower@solfut.com">ihavethepower@solfut.com</a>.</p>
      </div>
    </div>
  );
  if (!overview) return null;

  const { curso, encomenda_id, comprador_nome, quantidade_total, vagas_disponiveis, fichas } = overview;

  if (editing !== null) {
    return (
      <FichaForm
        token={token}
        ficha={editing}
        curso={curso}
        onSaved={async () => { setEditing(null); await reload(); }}
        onCancel={() => setEditing(null)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF8F2] p-4 sm:p-8" data-testid="curso-inscricao-public">
      <div className="max-w-3xl mx-auto">
        <header className="mb-6">
          <img src="/logo-ihtp-dark.svg" alt="IHTP" className="h-9 w-auto mb-4" data-testid="ihtp-logo" />
          <p className="text-xs uppercase tracking-[0.2em] text-[#6B7280]">IHTP — Inscrição em Curso</p>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#0F1829] mt-1">{curso?.NomeCurso}</h1>
          <p className="text-sm text-[#4B5563] mt-1">
            {fmtDate(curso?.DataInicio)} {curso?.DataFim && curso?.DataInicio !== curso?.DataFim ? `a ${fmtDate(curso?.DataFim)}` : ""}
            {curso?.Local ? ` · ${curso.Local}` : ""}
            {curso?.CargaHoraria ? ` · ${curso.CargaHoraria}h` : ""}
          </p>
          {curso?.TrainerNome && <p className="text-sm text-[#6B7280] mt-0.5">Formador: {curso.TrainerNome}</p>}
        </header>

        <div className="bg-white border border-[#E6E1D6] rounded-md p-4 mb-4">
          <p className="text-sm text-[#0F1829]">
            <strong>Encomenda nº {encomenda_id}</strong> · Comprador: {comprador_nome}
          </p>
          <p className="text-sm text-[#4B5563] mt-1">
            Esta encomenda dá direito a <strong>{quantidade_total} {quantidade_total === 1 ? "inscrição" : "inscrições"}</strong>.
            Faltam preencher: <strong>{vagas_disponiveis}</strong>.
          </p>
          {quantidade_total > 1 && (
            <p className="text-xs text-[#6B7280] mt-2 italic">
              Cada participante deve preencher o seu próprio formulário com NIF e email distintos. Pode partilhar este link com os outros inscritos.
            </p>
          )}
        </div>

        <section className="bg-white border border-[#E6E1D6] rounded-md overflow-hidden mb-4" data-testid="fichas-list">
          <h2 className="px-4 py-3 text-xs uppercase tracking-[0.2em] text-[#0F1829] bg-[#FAF8F2] border-b border-[#E6E1D6]">
            Participantes inscritos
          </h2>
          {fichas.length === 0 ? (
            <p className="p-6 text-sm text-center text-[#6B7280]">Ainda ninguém preencheu o formulário.</p>
          ) : (
            <ul>
              {fichas.map((f) => (
                <li key={f.ID_FichaInscricao} className="px-4 py-3 border-b last:border-b-0 border-[#F3F4F6] flex items-center justify-between gap-3 flex-wrap" data-testid={`ficha-row-${f.ID_FichaInscricao}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#0F1829] truncate">{f.NomeCompleto || "(sem nome)"}</p>
                    <p className="text-xs text-[#6B7280]">{f.Nif} · {f.Email}</p>
                    <div className="flex flex-wrap gap-1.5 mt-1.5 text-[10px]">
                      <Badge ok={f.preenchida}>📝 Formulário</Badge>
                      <Badge ok={f.ficha_assinada_recebida}>📎 Ficha assinada</Badge>
                      <Badge ok={f.contrato_assinado_recebido}>📎 Contrato assinado</Badge>
                      {f.is_menor && <Badge ok={f.menor_assinado_recebido}>📎 Decl. Menor</Badge>}
                      {f.bloqueada && <span className="px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600 border border-zinc-200">🔒 Bloqueada</span>}
                    </div>
                    {Array.isArray(f.campos_em_falta) && f.campos_em_falta.length > 0 && (
                      <p
                        data-testid={`ficha-campos-em-falta-${f.ID_FichaInscricao}`}
                        className="mt-1.5 text-xs text-[#B91C1C] font-medium leading-snug"
                      >
                        ⚠️ Falta preencher: {f.campos_em_falta.join(", ")}.
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <button onClick={() => setEditing(f)} data-testid={`ficha-open-${f.ID_FichaInscricao}`}
                      className="text-xs px-3 py-1.5 border border-[#0F1829] text-[#0F1829] rounded hover:bg-[#0F1829] hover:text-white transition">
                      {f.bloqueada ? "Ver / Documentos" : "Editar / Documentos"}
                    </button>
                    {f.codigo_barras && (
                      <button
                        onClick={() => setQrFicha(f)}
                        data-testid={`ficha-qr-${f.ID_FichaInscricao}`}
                        className="text-[11px] px-3 py-1.5 border border-emerald-600 text-emerald-700 rounded hover:bg-emerald-50 transition inline-flex items-center gap-1"
                        title="Ver / descarregar o QR Code de entrada no curso"
                      >
                        📱 Ver QR Code
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        {vagas_disponiveis > 0 && (
          <button
            onClick={() => setEditing({})} data-testid="add-ficha-btn"
            className="w-full px-4 py-3 bg-[#0F1829] text-white rounded-md hover:bg-[#1A2540] transition text-sm font-medium">
            + Adicionar nova inscrição ({vagas_disponiveis} {vagas_disponiveis === 1 ? "vaga" : "vagas"})
          </button>
        )}
      </div>
      {qrFicha && (
        <QrCodeModal token={token} ficha={qrFicha} cursoNome={curso?.NomeCurso} onClose={() => setQrFicha(null)} />
      )}
    </div>
  );
}

// ── Modal QR Code (alternativa ao email perdido) ─────────────────
function QrCodeModal({ token, ficha, cursoNome, onClose }) {
  const src = `${API}/api/public/cursos/inscricao/${token}/fichas/${ficha.ID_FichaInscricao}/qrcode`;
  return (
    <div
      role="dialog"
      data-testid="qr-modal"
      className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-sm w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280]">QR Code de entrada</p>
            <h3 className="text-base font-semibold text-[#0F1829] mt-1">{ficha.NomeCompleto || "Inscrição"}</h3>
            {cursoNome && <p className="text-xs text-[#6B7280] mt-0.5">{cursoNome}</p>}
          </div>
          <button
            onClick={onClose}
            data-testid="qr-modal-close"
            className="text-[#6B7280] hover:text-[#0F1829] -mr-2 -mt-2 p-1"
            aria-label="Fechar"
          >
            ✕
          </button>
        </div>
        <div className="flex justify-center mb-4 bg-white border border-[#E6E1D6] rounded p-3">
          <img
            src={src}
            alt={`QR Code de ${ficha.NomeCompleto}`}
            className="w-56 h-56 object-contain"
            data-testid="qr-modal-image"
          />
        </div>
        <p className="text-xs text-[#0F1829] font-mono text-center break-all mb-3">
          {ficha.codigo_barras}
        </p>
        <p className="text-[11px] text-[#6B7280] leading-relaxed mb-4">
          Apresente este QR Code à entrada do curso. Pode também guardá-lo no telemóvel — botão direito → guardar imagem,
          ou faça download abaixo.
        </p>
        <a
          href={src}
          download={`ihtp-qr-${ficha.ID_FichaInscricao}.png`}
          data-testid="qr-modal-download"
          className="block w-full text-center px-4 py-2 bg-[#0F1829] text-white rounded text-sm font-medium hover:bg-[#1A2540] transition"
        >
          Descarregar PNG
        </a>
      </div>
    </div>
  );
}

function Badge({ ok, children }) {
  return (
    <span className={`px-1.5 py-0.5 rounded border ${ok ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
      {ok ? "✓" : "⏳"} {children}
    </span>
  );
}

// ── Form de criação/edição ─────────────────────────────────────────

function FichaForm({ token, ficha, curso, onSaved, onCancel }) {
  const isNew = !ficha.ID_FichaInscricao;
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [lookups, setLookups] = useState(null);
  const [prefillInfo, setPrefillInfo] = useState(null); // { date } quando auto-prefill encontrou dados
  const fid = ficha.ID_FichaInscricao;
  const locked = ficha.bloqueada;

  useEffect(() => {
    if (!isNew && fid) {
      apiGet(`/${token}/fichas/${fid}`)
        .then((j) => setData(j.ficha || {}))
        .catch((e) => setError(e.message))
        .finally(() => setLoading(false));
    } else if (isNew) {
      // Auto-prefill silencioso a partir do ID_ClienteComprador do link
      apiGet(`/${token}/prefill`)
        .then((j) => {
          if (j.found) {
            setData((d) => ({ ...j.data, ...d })); // mantém o que user já editou
            setPrefillInfo({ date: j.previous_data_registo });
          }
        })
        .catch(() => {}); // silencioso: erro de prefill nunca bloqueia o form
    }
  }, [token, fid, isNew]);

  // Lookups públicos (cached pelo backend 10min)
  useEffect(() => {
    apiGet("/lookups")
      .then(setLookups)
      .catch(() => {
        // Em caso de erro, fallback para selects mínimos.
        setLookups({
          tipo_documento: [
            { id: "BI", label: "Bilhete de Identidade" },
            { id: "CC", label: "Cartão de Cidadão" },
            { id: "PA", label: "Passaporte" },
          ],
          sim_nao: [{ id: "S", label: "Sim" }, { id: "N", label: "Não" }],
        });
      });
  }, []);

  if (loading) return <div className="p-8 text-center text-sm text-[#6B7280]">A carregar ficha…</div>;

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const v = (k) => data[k] ?? "";

  // Detecta se o formando é menor (DataNascimento → idade < 18)
  const isMinor = (() => {
    const dn = v("DataNascimento");
    if (!dn) return false;
    const birth = new Date(String(dn).slice(0, 10));
    if (Number.isNaN(birth.getTime())) return false;
    const now = new Date();
    let age = now.getFullYear() - birth.getFullYear();
    const m = now.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < birth.getDate())) age--;
    return age < 18;
  })();

  // ─── Validações ──────────────────────────────────────────────────────
  // NIF Português (algoritmo módulo-11). Apenas aplicado quando o formando
  // (ou encarregado) é português.
  const isValidNifPT = (nif) => {
    if (!nif) return false;
    const s = String(nif).replace(/\s+/g, "");
    if (!/^\d{9}$/.test(s)) return false;
    // Primeiro dígito válido para pessoa singular/colectiva
    if (!"125689".includes(s[0]) && !["30", "45"].includes(s.slice(0, 2))) return false;
    let total = 0;
    for (let i = 0; i < 8; i++) total += Number(s[i]) * (9 - i);
    const mod = total % 11;
    const expected = mod < 2 ? 0 : 11 - mod;
    return Number(s[8]) === expected;
  };

  const isPortuguese = (v("ID_Nacionalidade") || "").toUpperCase() === "PRT";

  // Avisos não-bloqueantes (warnings amarelos por baixo do form)
  const warnings = [];
  const hoje = new Date().toISOString().slice(0, 10);
  if (v("DataValidadeDocumento") && v("DataValidadeDocumento") < hoje) {
    warnings.push("A validade do seu documento de identificação está ultrapassada — pode continuar, mas considere actualizar o documento.");
  }
  if (isMinor && v("EncarregadoCCValidade") && v("EncarregadoCCValidade") < hoje) {
    warnings.push("A validade do CC do Encarregado de Educação está ultrapassada — pode continuar, mas considere actualizar.");
  }

  const handleSave = async (e) => {
    e?.preventDefault?.();
    if (saving) return;
    if (!data.NomeCompleto || !data.Nif || !data.Email) {
      setError("Nome, NIF e Email são obrigatórios.");
      return;
    }
    const ev = await verifyEmail(data.Email);
    if (!ev.valid) {
      setError(ev.suggestion ? `${ev.message} Quis dizer ${ev.suggestion}?` : ev.message);
      return;
    }
    if (!data.PaisOrigem) {
      setError("País de Origem é obrigatório.");
      return;
    }
    if (!data.ID_SectorProfissionalEmpresa) {
      setError("Sector Profissional é obrigatório.");
      return;
    }
    if (isPortuguese && !isValidNifPT(data.Nif)) {
      setError("NIF inválido. Verifique o número de contribuinte português (9 dígitos).");
      return;
    }
    if (isMinor) {
      const missing = ["EncarregadoNome", "EncarregadoNif", "EncarregadoCC", "EncarregadoCCValidade"]
        .filter((k) => !String(data[k] || "").trim());
      if (missing.length > 0) {
        setError("Formando menor de 18 anos: Nome, NIF, Cartão de Cidadão e Validade do Encarregado de Educação são todos obrigatórios.");
        return;
      }
      // Validar NIF do Encarregado (assumimos PT — se internacional, o admin
      // pode regularizar manualmente)
      if (data.EncarregadoNif && !isValidNifPT(data.EncarregadoNif)) {
        setError("NIF do Encarregado de Educação inválido (9 dígitos).");
        return;
      }
    }
    setSaving(true); setError(null);
    try {
      if (isNew) await apiJson(`/${token}/fichas`, "POST", data);
      else await apiJson(`/${token}/fichas/${fid}`, "PUT", data);
      onSaved();
    } catch (err) { setError(err.message); setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-[#FAF8F2] p-4 sm:p-8" data-testid="ficha-form-page">
      <div className="max-w-3xl mx-auto">
        <header className="mb-6">
          <img src="/logo-ihtp-dark.svg" alt="IHTP" className="h-8 w-auto mb-3" data-testid="ihtp-logo-form" />
          <button onClick={onCancel} className="text-xs text-[#0F1829] hover:underline mb-2">← Voltar</button>
          <h1 className="text-2xl font-bold text-[#0F1829]">
            {isNew ? "Nova inscrição" : `Inscrição #${fid}`}
          </h1>
          <p className="text-sm text-[#6B7280] mt-1">{curso?.NomeCurso}</p>
          {locked && (
            <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
              🔒 Esta inscrição está bloqueada para edição porque já foram carregados PDFs assinados. Se precisa de alterar, contacte o suporte.
            </div>
          )}
        </header>

        {error && <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700" data-testid="form-error">{error}</div>}

        {warnings.length > 0 && (
          <div className="mb-3 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800" data-testid="form-warnings">
            {warnings.map((w, idx) => (
              <p key={idx} className="flex items-start gap-1.5">
                <span>⚠</span><span>{w}</span>
              </p>
            ))}
          </div>
        )}

        {isNew && !locked && prefillInfo && (
          <div
            className="mb-3 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded text-[11px] text-emerald-800 flex items-start gap-2"
            data-testid="prefill-auto-info"
          >
            <span className="text-base leading-none mt-[-1px]">✓</span>
            <span>
              <strong>Dados pré-preenchidos</strong> da sua inscrição anterior
              {prefillInfo.date ? ` (${new Date(prefillInfo.date).toLocaleDateString("pt-PT")})` : ""}.
              {" "}Reveja os campos abaixo antes de gravar — se for outra pessoa a inscrever-se, ajuste os dados.
            </span>
          </div>
        )}

        <form onSubmit={handleSave} noValidate className="bg-white border border-[#E6E1D6] rounded-md p-4 grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          <Field label="Nome completo *" value={v("NomeCompleto")} onChange={(x) => set("NomeCompleto", x)} disabled={locked} required testid="inp-nome-completo" />
          <Field label="NIF *" value={v("Nif")} onChange={(x) => set("Nif", x)} disabled={locked} required testid="inp-nif" />
          <Field label="Email *" type="email" value={v("Email")} onChange={(x) => set("Email", x)} disabled={locked} required testid="inp-email" />
          <Field label="Telefone" value={v("Telefone")} onChange={(x) => set("Telefone", x)} disabled={locked} testid="inp-telefone" />
          <Field label="Data de nascimento" type="date" value={(v("DataNascimento") || "").slice(0, 10)} onChange={(x) => set("DataNascimento", x)} disabled={locked} testid="inp-data-nascimento" />
          <Select label="Nacionalidade" value={v("ID_Nacionalidade")} onChange={(x) => set("ID_Nacionalidade", x)} options={lookups?.nacionalidade} disabled={locked} testid="sel-nacionalidade" />
          <Select label="País de Origem *" value={v("PaisOrigem")} onChange={(x) => set("PaisOrigem", x)} options={lookups?.nacionalidade} disabled={locked} required testid="sel-pais-origem" />
          <Field label="Morada" value={v("Morada")} onChange={(x) => set("Morada", x)} disabled={locked} className="sm:col-span-2" testid="inp-morada" />
          <Field label="Código Postal" value={v("CodigoPostal")} onChange={(x) => set("CodigoPostal", x)} disabled={locked} testid="inp-codigo-postal" />
          <Field label="Localidade" value={v("Localidade")} onChange={(x) => set("Localidade", x)} disabled={locked} testid="inp-localidade" />
          <Field label="Distrito" value={v("DistritoNacionalidade")} onChange={(x) => set("DistritoNacionalidade", x)} disabled={locked} testid="inp-distrito" />
          <Field label="Concelho" value={v("ConcelhoNacionalidade")} onChange={(x) => set("ConcelhoNacionalidade", x)} disabled={locked} testid="inp-concelho" />
          <Select label="Tipo de documento" value={v("ID_TipoDocumento")} onChange={(x) => set("ID_TipoDocumento", x)} options={lookups?.tipo_documento} disabled={locked} testid="sel-tipo-documento" />
          <Field label="Nº documento" value={v("NumeroDocumento")} onChange={(x) => set("NumeroDocumento", x)} disabled={locked} testid="inp-numero-documento" />
          <Field label="Validade documento" type="date" value={(v("DataValidadeDocumento") || "").slice(0, 10)} onChange={(x) => set("DataValidadeDocumento", x)} disabled={locked} testid="inp-validade-documento" />
          <Select label="Habilitação" value={v("ID_Habilitacao")} onChange={(x) => set("ID_Habilitacao", x)} options={lookups?.habilitacao} disabled={locked} testid="sel-habilitacao" />
          <Select label="Condição profissional" value={v("ID_TipoProfissao")} onChange={(x) => set("ID_TipoProfissao", x)} options={lookups?.tipo_profissao} disabled={locked} testid="sel-tipo-profissao" />
          <Field label="Profissão" value={v("Profissao")} onChange={(x) => set("Profissao", x)} disabled={locked} testid="inp-profissao" />
          <Field label="Empresa" value={v("Empresa")} onChange={(x) => set("Empresa", x)} disabled={locked} testid="inp-empresa" />
          <Select label="Sector profissional *" value={v("ID_SectorProfissionalEmpresa")} onChange={(x) => set("ID_SectorProfissionalEmpresa", x)} options={lookups?.sector} disabled={locked} required testid="sel-sector-profissional" />
          <Select label="Como nos conheceu?" value={v("ID_Referencia")} onChange={(x) => set("ID_Referencia", x)} options={lookups?.referencia} disabled={locked} testid="sel-referencia" />
          <Select label="Aceita DGERT?" value={v("ConsentDGERT")} onChange={(x) => set("ConsentDGERT", x)} options={lookups?.sim_nao} disabled={locked} testid="sel-consent-dgert" />
          <div>
            <Select label="Aceita Regulamento?" value={v("ConsentRegulamento")} onChange={(x) => set("ConsentRegulamento", x)} options={lookups?.sim_nao} disabled={locked} testid="sel-consent-regulamento" />
            <a
              href={`${API}/api/public/cursos/inscricao/regulamento-formacao`}
              target="_blank"
              rel="noreferrer"
              data-testid="link-regulamento-formacao"
              className="mt-1 inline-flex items-center gap-1 text-[11px] text-[#0F1829] underline hover:no-underline"
            >
              📄 Consultar Regulamento de Funcionamento da Formação (PDF)
            </a>
          </div>
          <Select label="Aceita registo site?" value={v("ConsentRegistoSite")} onChange={(x) => set("ConsentRegistoSite", x)} options={lookups?.sim_nao} disabled={locked} testid="sel-consent-registo-site" />

          {isMinor && (
            <div className="sm:col-span-2 mt-2 p-3 bg-[#FFF7E6] border border-[#F0D8A8] rounded-md" data-testid="encarregado-section">
              <p className="text-xs font-bold text-[#7A5C18] mb-1">
                Encarregado de Educação (formando menor de 18 anos)
              </p>
              <p className="text-[11px] text-[#8B7340] mb-2">
                Todos os campos abaixo são obrigatórios. A Declaração de Menor terá de ser assinada pelo Encarregado de Educação (não pelo formando).
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Nome do Encarregado *" value={v("EncarregadoNome")} onChange={(x) => set("EncarregadoNome", x)} disabled={locked} required testid="inp-encarregado-nome" />
                <Field label="NIF do Encarregado *" value={v("EncarregadoNif")} onChange={(x) => set("EncarregadoNif", x)} disabled={locked} required testid="inp-encarregado-nif" />
                <Field label="Nº Cartão de Cidadão *" value={v("EncarregadoCC")} onChange={(x) => set("EncarregadoCC", x)} disabled={locked} required testid="inp-encarregado-cc" />
                <Field label="Validade do CC *" type="date" value={(v("EncarregadoCCValidade") || "").slice(0, 10)} onChange={(x) => set("EncarregadoCCValidade", x)} disabled={locked} required testid="inp-encarregado-cc-validade" />
              </div>
            </div>
          )}

          {!locked && (
            <div className="sm:col-span-2 flex gap-2 pt-3 border-t border-[#E6E1D6]">
              <button type="submit" disabled={saving} data-testid="ficha-save-btn"
                className="px-4 py-2 bg-[#0F1829] text-white rounded text-sm disabled:opacity-50">
                {saving ? "A guardar…" : (isNew ? "Criar inscrição" : "Guardar alterações")}
              </button>
              <button type="button" onClick={onCancel} className="px-4 py-2 border border-[#E6E1D6] rounded text-sm hover:bg-[#FAF8F2]">
                Cancelar
              </button>
            </div>
          )}
        </form>

        {!isNew && data.ID_FichaInscricao && (
          <>
            <DocumentsSection token={token} fichaId={fid} ficha={ficha} />
            <CertificacaoSection token={token} fichaId={fid} />
          </>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", disabled, required, placeholder, className = "", testid }) {
  return (
    <label className={`flex flex-col gap-1 ${className}`}>
      <span className="text-[10px] uppercase tracking-[0.15em] text-[#6B7280]">{label}</span>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} required={required} placeholder={placeholder}
        data-testid={testid}
        className="px-3 py-2 text-sm border border-[#E6E1D6] rounded outline-none focus:border-[#0F1829] disabled:bg-zinc-50 disabled:text-zinc-500" />
    </label>
  );
}

function Select({ label, value, onChange, options, disabled, required, testid }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] uppercase tracking-[0.15em] text-[#6B7280]">{label}</span>
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} disabled={disabled} required={required}
        data-testid={testid}
        className="px-3 py-2 text-sm border border-[#E6E1D6] rounded outline-none focus:border-[#0F1829] disabled:bg-zinc-50">
        <option value="">— escolha —</option>
        {(options || []).map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
      </select>
    </label>
  );
}

// ── Documents (PDF download + upload assinados) ────────────────────

function DocumentsSection({ token, fichaId, ficha }) {
  const tipos = [
    { tipo: "ficha", label: "Ficha de Inscrição", required: true },
    { tipo: "contrato", label: "Contrato de Formação", required: true },
    ...(ficha?.is_menor ? [{ tipo: "menor", label: "Declaração de Menor", required: true }] : []),
  ];
  const formIncomplete = ficha?.form_completo === false;
  const missingFields = ficha?.campos_em_falta || [];
  return (
    <section className="bg-white border border-[#E6E1D6] rounded-md p-4" data-testid="documents-section">
      <h2 className="text-sm font-bold text-[#0F1829] mb-1">Documentos</h2>
      {formIncomplete ? (
        <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800"
          data-testid="documents-form-incomplete-banner">
          <p className="font-semibold mb-1">⚠ Preencha o formulário antes de descarregar/enviar documentos</p>
          <p className="mb-1">
            Para gerar a Ficha, o Contrato e a Declaração de Menor (quando aplicável), o formulário acima
            tem de estar completamente preenchido. Sem isto não é possível fazer mailmerge dos dados.
          </p>
          {missingFields.length > 0 && (
            <p>Em falta: <strong>{missingFields.join(", ")}</strong>.</p>
          )}
        </div>
      ) : (
        <p className="text-xs text-[#6B7280] mb-3">
          Faça download de cada documento, assine e envie o PDF assinado de volta — pode usar o botão {`"Carregar PDF assinado"`} abaixo, ou enviar por email para
          {" "}<a className="underline" href="mailto:ihavethepower@solfut.com">ihavethepower@solfut.com</a>, até 24 horas antes do início do curso.
        </p>
      )}
      <div className="space-y-3">
        {tipos.map((t) => (
          <DocumentRow key={t.tipo} token={token} fichaId={fichaId} tipo={t.tipo}
            label={t.label} disabled={formIncomplete} />
        ))}
      </div>
    </section>
  );
}

function DocumentRow({ token, fichaId, tipo, label, disabled = false }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const handleDownload = async () => {
    if (disabled) return;
    setBusy(true); setMsg(null);
    try {
      const r = await fetch(`${API}/api/public/cursos/inscricao/${token}/fichas/${fichaId}/pdf/${tipo}`);
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || `HTTP ${r.status}`);
      }
      const isDocxFallback = r.headers.get("x-document-format") === "docx-fallback";
      const blob = await r.blob();
      const ext = isDocxFallback ? "docx" : "pdf";
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${tipo}_${fichaId}.${ext}`;
      // Firefox/Safari exigem que o elemento esteja no DOM para o `click()`
      // programático disparar o download. Além disso, o revokeObjectURL
      // tem de ser adiado — se for síncrono, o browser cancela o download
      // antes de o iniciar. Bug 2026-07-16: Ana Alexandra Rodrigues não
      // conseguia descarregar ficha/contrato porque este anchor nunca era
      // adicionado ao DOM (funcionava intermitentemente no Chrome, sempre
      // falhava em Firefox/Edge).
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        try { document.body.removeChild(a); } catch { /* já removido */ }
        URL.revokeObjectURL(url);
      }, 1500);
      if (isDocxFallback) {
        setMsg("Descarregado em Word (.docx). Abra no Word, assine ou imprima, e envie o PDF assinado abaixo.");
      }
    } catch (e) { setMsg("Erro: " + e.message); }
    finally { setBusy(false); }
  };
  const handleUpload = async (file) => {
    if (disabled) return;
    if (!file) return;
    setBusy(true); setMsg(null);
    try {
      await apiUpload(`/${token}/fichas/${fichaId}/upload/${tipo}`, file);
      setMsg("✓ Enviado!"); setTimeout(() => window.location.reload(), 800);
    } catch (e) { setMsg("Erro: " + e.message); }
    finally { setBusy(false); }
  };
  const title = disabled ? "Preencha o formulário acima antes de usar este botão" : undefined;
  return (
    <div className="flex items-center gap-2 p-3 bg-[#FAF8F2] border border-[#E6E1D6] rounded" data-testid={`doc-row-${tipo}`}>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-[#0F1829]">{label}</p>
        {msg && <p className="text-[10px] text-[#6B7280] mt-0.5">{msg}</p>}
      </div>
      <button type="button" onClick={handleDownload} disabled={busy || disabled} title={title}
        data-testid={`doc-download-${tipo}`}
        className="text-xs px-3 py-1.5 border border-[#0F1829] text-[#0F1829] rounded hover:bg-[#0F1829] hover:text-white disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-[#0F1829]">
        Download PDF
      </button>
      <label title={title}
        data-testid={`doc-upload-${tipo}`}
        className={`text-xs px-3 py-1.5 bg-[#0F1829] text-white rounded ${disabled ? "opacity-40 cursor-not-allowed" : "hover:bg-[#1A2540] cursor-pointer"}`}>
        Carregar assinado
        <input type="file" accept="application/pdf" hidden disabled={disabled || busy}
          onChange={(e) => handleUpload(e.target.files?.[0])} />
      </label>
    </div>
  );
}


// ── Documentos da Certificação (License Agreement, Diploma, Coaching…) ──────
//
// Só renderiza se o curso desta ficha tem `TipoCertificacao` configurado.
// Auto-descobre os docs necessários chamando o endpoint público
// `/api/public/cursos/inscricao/{token}/fichas/{fid}/certificacao/documentos`.

function CertificacaoSection({ token, fichaId }) {
  const [state, setState] = useState({ loading: true, docs: [], tipo: null, cursoNome: "" });
  const [uploading, setUploading] = useState({});
  const [msgs, setMsgs] = useState({});

  const load = async () => {
    try {
      const r = await fetch(
        `${API}/api/public/cursos/inscricao/${token}/fichas/${fichaId}/certificacao/documentos`,
        { credentials: "omit" },
      );
      if (!r.ok) {
        setState({ loading: false, docs: [], tipo: null, cursoNome: "" });
        return;
      }
      const j = await r.json();
      setState({
        loading: false,
        docs: j.docs || [],
        tipo: j.ficha?.TipoCertificacao || null,
        cursoNome: j.ficha?.NomeCurso || "",
      });
    } catch {
      setState({ loading: false, docs: [], tipo: null, cursoNome: "" });
    }
  };
  useEffect(() => { load(); }, [fichaId, token]);

  const upload = async (tipo, file) => {
    if (!file) return;
    setUploading((u) => ({ ...u, [tipo]: true }));
    setMsgs((m) => ({ ...m, [tipo]: null }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await fetch(
        `${API}/api/public/cursos/inscricao/${token}/fichas/${fichaId}/certificacao/documentos/${tipo}`,
        { method: "POST", credentials: "omit", body: fd },
      );
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || `HTTP ${r.status}`);
      }
      setMsgs((m) => ({ ...m, [tipo]: "✓ Enviado!" }));
      await load();
    } catch (e) {
      setMsgs((m) => ({ ...m, [tipo]: "Erro: " + e.message }));
    } finally {
      setUploading((u) => ({ ...u, [tipo]: false }));
    }
  };

  if (state.loading) return null;
  if (!state.tipo || state.docs.length === 0) return null;

  const done = state.docs.filter((d) => d.uploaded).length;

  return (
    <section
      className="bg-white border border-[#A8843C] rounded-md p-4 mt-3"
      data-testid={`certificacao-section-${fichaId}`}
    >
      <div className="flex items-center gap-2 mb-2">
        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#A8843C] text-white text-xs font-bold">★</span>
        <h2 className="text-sm font-bold text-[#7A5B14]">Documentos da Certificação</h2>
        <span className="ml-auto text-xs text-[#6B7280]">{done}/{state.docs.length} entregues</span>
      </div>
      <p className="text-xs text-[#6B7280] mb-3">
        Documentos obrigatórios para a conclusão da certificação. Descarregue os templates disponíveis,
        assine/preencha e envie de volta. Ficheiros ZIP grandes são aceites (até 200 MB).
      </p>
      <div className="space-y-2">
        {state.docs.map((d) => (
          <CertDocRow
            key={d.tipo}
            doc={d}
            busy={!!uploading[d.tipo]}
            msg={msgs[d.tipo]}
            onUpload={(file) => upload(d.tipo, file)}
          />
        ))}
      </div>
    </section>
  );
}

function CertDocRow({ doc, busy, msg, onUpload }) {
  const [hoverPreview, setHoverPreview] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  return (
    <div
      data-testid={`cert-doc-row-${doc.tipo}`}
      className="flex items-start gap-2 p-3 bg-[#FAF8F2] border border-[#E6E1D6] rounded"
    >
      <span
        className={`mt-1 inline-block w-2.5 h-2.5 rounded-full shrink-0 ${
          doc.uploaded ? "bg-green-500" : "bg-amber-500"
        }`}
        title={doc.uploaded ? "Entregue" : "Pendente"}
      />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-[#0F1829]">{doc.label}</p>
        {doc.hint && !doc.uploaded && (
          <p className="text-[11px] text-[#6B7280] mt-0.5">{doc.hint}</p>
        )}
        {doc.uploaded && doc.data_upload && (
          <p className="text-[11px] text-[#6B7280] mt-0.5">
            Enviado em {new Date(doc.data_upload).toLocaleDateString("pt-PT")}
          </p>
        )}
        {msg && <p className="text-[11px] mt-0.5 text-[#6B7280]">{msg}</p>}
      </div>
      {doc.template_url && (
        <div
          className="relative"
          onMouseEnter={() => setHoverPreview(true)}
          onMouseLeave={() => setHoverPreview(false)}
        >
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            data-testid={`cert-doc-template-${doc.tipo}`}
            className="text-xs px-3 py-1.5 border border-[#0F1829] text-[#0F1829] rounded hover:bg-[#0F1829] hover:text-white inline-flex items-center gap-1"
          >
            📄 Ver template
          </button>
          {hoverPreview && !modalOpen && (
            <div
              data-testid={`cert-doc-template-preview-${doc.tipo}`}
              className="hidden md:block absolute right-0 top-full mt-2 z-40 bg-white border-2 border-[#A8843C] rounded-lg shadow-2xl overflow-hidden"
              style={{ width: 320, height: 420 }}
            >
              <div className="text-[10px] font-medium text-[#7A5B14] px-2 py-1 bg-[#FAF8F2] border-b border-[#E6E1D6] flex items-center justify-between">
                <span className="truncate">Pré-visualização: {doc.label}</span>
                <span className="text-[10px] text-[#A8843C] shrink-0 ml-2">clica para ampliar</span>
              </div>
              <iframe
                src={`${doc.template_url}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                title="Preview"
                className="w-full pointer-events-none"
                style={{ border: 0, height: "calc(100% - 24px)" }}
              />
            </div>
          )}
        </div>
      )}
      {doc.uploaded && doc.url && (
        <a
          href={doc.url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`cert-doc-view-${doc.tipo}`}
          className="text-xs px-3 py-1.5 border border-green-600 text-green-700 rounded hover:bg-green-600 hover:text-white"
        >
          Ver enviado
        </a>
      )}
      <label
        data-testid={`cert-doc-upload-${doc.tipo}`}
        className={`text-xs px-3 py-1.5 bg-[#A8843C] text-white rounded ${
          busy ? "opacity-40 cursor-not-allowed" : "hover:bg-[#8B6E32] cursor-pointer"
        }`}
      >
        {busy ? "A enviar…" : doc.uploaded ? "Substituir" : "Carregar"}
        <input
          type="file"
          accept=".pdf,.docx,.zip,.jpg,.jpeg,.png"
          hidden
          disabled={busy}
          onChange={(e) => onUpload(e.target.files?.[0])}
        />
      </label>
      {modalOpen && doc.template_url && (
        <TemplatePreviewModal
          url={doc.template_url}
          title={doc.label}
          onClose={() => setModalOpen(false)}
        />
      )}
    </div>
  );
}

// ── Modal fullscreen para leitura do template (mobile-friendly) ─────────────
// Renderiza PDFs via `react-pdf` (PDF.js) para garantir compatibilidade
// universal (iOS Safari, Android Chrome, Firefox, etc.), com paginação
// nativa e zoom. Para formatos não-PDF (DOCX/DOC) mostra CTA de download.
//
// Fecha por: clique no backdrop, tecla ESC ou botão ×. Bloqueia scroll do
// body enquanto aberto.

import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

// Worker do PDF.js — servido de CDN público para não precisar de copy no build.
// Usa a mesma versão que o pacote pdfjs-dist instalado.
pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

function TemplatePreviewModal({ url, title, onClose }) {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [loadError, setLoadError] = useState(false);
  const [containerWidth, setContainerWidth] = useState(0);
  const containerRef = useRef(null);

  const isPdf = /\.pdf(\?|$)/i.test(url) || url.includes("/template");

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (numPages && e.key === "ArrowRight") setPageNumber((p) => Math.min(p + 1, numPages));
      if (numPages && e.key === "ArrowLeft") setPageNumber((p) => Math.max(p - 1, 1));
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose, numPages]);

  useEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width || 0;
      setContainerWidth(Math.max(280, Math.min(1100, w - 24)));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div
      data-testid="template-modal"
      className="fixed inset-0 z-[70] bg-black/85 flex items-stretch justify-stretch sm:items-center sm:justify-center sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white w-full h-full sm:max-w-5xl sm:h-[92vh] sm:rounded-lg overflow-hidden flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center gap-2 px-3 py-2 sm:px-4 sm:py-3 border-b border-[#E6E1D6] bg-[#FAF8F2] shrink-0">
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-widest text-[#A8843C] font-bold">
              Pré-visualização do template
            </p>
            <p className="text-sm font-medium text-[#0F1829] truncate">{title}</p>
          </div>
          <a
            href={url}
            download
            data-testid="template-modal-download"
            className="text-xs px-3 py-1.5 bg-[#A8843C] text-white rounded hover:bg-[#8B6E32] whitespace-nowrap"
          >
            ⬇ Descarregar
          </a>
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="template-modal-open"
            className="hidden sm:inline text-xs px-3 py-1.5 border border-[#0F1829] text-[#0F1829] rounded hover:bg-[#0F1829] hover:text-white whitespace-nowrap"
          >
            Nova tab
          </a>
          <button
            type="button"
            onClick={onClose}
            data-testid="template-modal-close"
            aria-label="Fechar"
            className="text-2xl leading-none w-9 h-9 rounded-full text-[#0F1829] hover:bg-[#0F1829] hover:text-white transition"
          >
            ×
          </button>
        </header>

        <div
          ref={containerRef}
          className="flex-1 min-h-0 overflow-auto bg-[#333] flex flex-col items-center py-4 px-2"
        >
          {!isPdf || loadError ? (
            <div className="text-center text-white/90 px-6 pt-16">
              <p className="text-lg mb-2">Pré-visualização não disponível</p>
              <p className="text-sm text-white/60 mb-4">
                {loadError
                  ? "Ocorreu um erro ao carregar o documento."
                  : "Este formato não suporta pré-visualização — usa o botão Descarregar."}
              </p>
              <a
                href={url}
                download
                className="inline-block px-5 py-2 bg-[#A8843C] text-white rounded hover:bg-[#8B6E32]"
              >
                ⬇ Descarregar
              </a>
            </div>
          ) : (
            <Document
              file={url}
              onLoadSuccess={({ numPages: n }) => { setNumPages(n); setLoadError(false); }}
              onLoadError={() => setLoadError(true)}
              loading={
                <div className="text-white/70 text-sm py-16" data-testid="template-modal-loading">
                  A carregar PDF…
                </div>
              }
              error={
                <div className="text-white/70 text-sm py-16">
                  Erro ao carregar. Usa &quot;Descarregar&quot; acima.
                </div>
              }
              className="max-w-full"
            >
              {numPages && containerWidth > 0 && (
                <Page
                  pageNumber={pageNumber}
                  width={containerWidth}
                  renderAnnotationLayer={false}
                  renderTextLayer={false}
                  className="shadow-xl mb-3"
                />
              )}
            </Document>
          )}
        </div>

        {isPdf && !loadError && numPages > 1 && (
          <footer
            className="shrink-0 border-t border-[#E6E1D6] bg-[#FAF8F2] px-3 py-2 flex items-center gap-2 justify-center"
            data-testid="template-modal-pager"
          >
            <button
              type="button"
              onClick={() => setPageNumber((p) => Math.max(p - 1, 1))}
              disabled={pageNumber <= 1}
              data-testid="template-modal-prev"
              className="px-3 py-1.5 text-xs bg-white border border-[#E6E1D6] rounded hover:bg-[#F5F2EB] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              ← Anterior
            </button>
            <span className="text-xs text-[#6B7280] font-medium min-w-[80px] text-center">
              Página <b>{pageNumber}</b> de {numPages}
            </span>
            <button
              type="button"
              onClick={() => setPageNumber((p) => Math.min(p + 1, numPages))}
              disabled={pageNumber >= numPages}
              data-testid="template-modal-next"
              className="px-3 py-1.5 text-xs bg-white border border-[#E6E1D6] rounded hover:bg-[#F5F2EB] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Seguinte →
            </button>
          </footer>
        )}
      </div>
    </div>
  );
}
